import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { searchNovels, getHotRanking } from "@/lib/novel";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import NovelCard from "@/components/base/NovelCard";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = (searchParams.get("q") || "").trim();
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => searchNovels(keyword), [keyword]);
  const hot = useMemo(() => getHotRanking(6), []);

  useDocumentMeta(
    keyword ? `${keyword}搜索结果 - 御书屋` : "搜索小说 - 御书屋",
    keyword
      ? `御书屋“${keyword}”的搜索结果，共找到 ${results.length} 部相关小说，支持在线阅读与章节浏览。`
      : "在御书屋搜索你想看的小说，支持按书名、作者、分类关键词查找。",
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const q = input.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <div className="mb-5">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
      </div>

      <header className="mb-8 rounded-lg border border-background-200 bg-background-50 p-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {keyword ? `“${keyword}” 的搜索结果` : "搜索小说"}
        </h1>
        <p className="mt-1 text-sm text-foreground-500">
          {keyword
            ? `共找到 ${results.length} 部相关小说`
            : "输入书名、作者或分类关键词，快速找到你想看的小说"}
        </p>
        <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-2.5">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-lg"></i>
            </span>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="搜索书名 / 作者 / 分类"
              className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-accent-500 px-8 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
          >
            搜索
          </button>
        </form>
      </header>

      {keyword && results.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {results.map((novel) => (
            <NovelCard key={novel.id} novel={novel} />
          ))}
        </div>
      )}

      {keyword && results.length === 0 && (
        <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-300">
            <i className="ri-search-eye-line"></i>
          </span>
          <h2 className="font-heading text-lg font-semibold text-foreground-800">
            没有找到与“{keyword}”相关的小说
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            换个关键词试试，或者看看下面的热门推荐。
          </p>
          <div className="mt-6 w-full max-w-3xl px-4 text-left">
            <h3 className="mb-3 font-heading text-base font-semibold text-foreground-900">
              热门搜索推荐
            </h3>
            <div className="flex flex-wrap gap-2">
              {hot.map((novel) => (
                <Link
                  key={novel.id}
                  to={`/detail/${novel.id}`}
                  className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600"
                >
                  {novel.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {!keyword && (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="font-heading text-lg font-semibold text-foreground-900">热门搜索</h2>
          <p className="mt-1 text-sm text-foreground-500">大家都在看这些小说</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {hot.map((novel) => (
              <Link
                key={novel.id}
                to={`/detail/${novel.id}`}
                className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600"
              >
                {novel.title}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}